import React from 'react';

import type { DndDragHandleProps } from '~/components/core/DndOrderedList/DndOrderedList.types';
import Text from '~/components/core/TextComponents/Text';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import ReorderDragHandle from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/components/ReorderDragHandle';
import Switch from '~/components/ui/atoms/Switch';
import type { FundAccountRuleInfo } from '~/server_shared/openapi/data-contracts';
import cn from '~/Utils/cn';
import { getLobDescription } from '~/Utils/lobUtils';

import { getPaymentMethodLabel } from '../paymentMethods';

import { gridClassFor } from './FundAccountRulesTableHeader';

interface FundAccountRuleRowProps {
  rule: FundAccountRuleInfo;
  dragHandleProps: DndDragHandleProps;
  /** True while filters are active or a write is in flight — the handle is then inert. */
  isDragDisabled: boolean;
  isProcessing: boolean;
  showSubOrgColumn: boolean;
  subOrgIdToName: Record<number, string>;
  lobConfigurationsDict: Record<string, unknown>;
  onEdit: (rule: FundAccountRuleInfo) => void;
  onDelete: (rule: FundAccountRuleInfo) => void | Promise<void>;
  onEnabledChanged: (rule: FundAccountRuleInfo) => void | Promise<void>;
}

// `content` is a plain string, not a `ReactNode`: every cell below is a joined label, and `Text`
// declares `children` as `PropTypes.node.isRequired`, so a possibly-`undefined` `ReactNode` is not
// assignable to it.
const cell = (content: string, key: string) => (
  <Text key={key} variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR}>
    {content}
  </Text>
);

const FundAccountRuleRow: React.FC<FundAccountRuleRowProps> = ({
  rule,
  dragHandleProps,
  isDragDisabled,
  isProcessing,
  showSubOrgColumn,
  subOrgIdToName,
  lobConfigurationsDict,
  onEdit,
  onDelete,
  onEnabledChanged,
}) => {
  const subOrgLabel = rule.scope?.is_all_sub_orgs
    ? 'All'
    : // A sub-organization the org no longer lists (removed since the rule was written) has no
      // name; fall back to its id so the cell still says the rule is scoped, rather than
      // dropping the entry and rendering blank — blank reads like "no scope" next to an "All"
      // row. Same fallback the LOB cell below makes for an unknown LOB key.
      (rule.scope?.sub_organization_ids ?? []).map((id) => subOrgIdToName[id] ?? `Sub-Org ${id}`).join(', ');
  const lobLabel = rule.scope?.is_all_lobs
    ? 'All'
    : // A LOB missing from the org config has no description; fall back to its key so the cell
      // names the LOB instead of rendering blank.
      (rule.scope?.lobs ?? []).map((lobKey) => getLobDescription(lobKey, lobConfigurationsDict) ?? lobKey).join(', ');
  const paymentMethodsLabel = rule.payment_methods?.is_all
    ? 'All'
    : (rule.payment_methods?.values ?? []).map(getPaymentMethodLabel).join(', ');

  const actionOptions = [
    { onClick: () => onEdit(rule), node: 'Edit', key: 'editRule' },
    {
      onClick: () => onDelete(rule),
      withConfirmProps: {
        title: 'Delete Fund Account Rule?',
        contentText: `Are you sure you want to delete "${rule.name}"?`,
        primaryButtonName: 'Delete',
      },
      node: 'Delete',
      key: 'deleteRule',
    },
  ];

  return (
    <div
      className={cn(
        gridClassFor(showSubOrgColumn),
        'border-0 border-b border-solid border-slate-300 bg-white py-8',
        // A disabled rule stays in the priority list (the BE returns it) but reads as inactive.
        // Dimming the children rather than the row keeps the row's own background opaque, so a
        // disabled row still reads solid while being dragged over the list behind it.
        rule.is_enabled ? '' : '*:opacity-60'
      )}
    >
      <ReorderDragHandle dragHandleProps={dragHandleProps} disabled={isDragDisabled} />
      {cell(rule.name, 'name')}
      {showSubOrgColumn ? cell(subOrgLabel, 'subOrgs') : null}
      {cell(lobLabel, 'lob')}
      {cell(paymentMethodsLabel, 'paymentMethods')}
      {cell(rule.account_number, 'accountNumber')}
      {/* Named after the rule it belongs to: in a reorderable list a positional selector follows
          whichever row happens to sit at that index, which changes as soon as the order does. */}
      <Switch
        aria-label={`Enable — ${rule.name}`}
        onChange={() => onEnabledChanged(rule)}
        disabled={isProcessing}
        checked={!!rule.is_enabled}
      />
      <div>
        {/* Disabled alongside the switch above: Delete stayed clickable while a delete, enable or
            reorder write was still in flight. */}
        <ThreeDotsMenu options={actionOptions} disabled={isProcessing} />
      </div>
    </div>
  );
};

export default FundAccountRuleRow;
